import {Component, HostListener, Inject, OnInit} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from "@angular/material/dialog";
import {FormControl, FormGroup, Validators} from "@angular/forms";
import {Store} from '@ngrx/store';
import {CartRequestDetails} from '../../../../../@domain/cart-request-details';
import * as fromApp from '../../../../../app.reducer';
import {ADD_FOOD_TO_CART} from "../../../store/place-order/place-order.action";
import {FormHelper, KeyString} from '../../../../../../../projects/vendors/src/lib/services/form-helper';
import {FoodItemQuantity} from "../../../../../@domain/food-item-quantity";
import {ActivePos} from "../../../../../@domain/single-menu-group";

@Component({
    selector: 'lib-add-to-cart-dialog',
    templateUrl: './add-to-cart-dialog.component.html',
    styleUrl: './add-to-cart-dialog.component.css',
    standalone: false
})
export class AddToCartDialogComponent implements OnInit {

  @HostListener('document:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent) {
    // Skip if user is typing in textarea
    const activeElement = document.activeElement;
    const isTextareaFocused = activeElement instanceof HTMLTextAreaElement;

    // Escape - close dialog
    if (event.key === 'Escape') {
      event.preventDefault();
      this.dialogRef.close(true);
      return;
    }

    // Enter - submit (unless in textarea)
    if (event.key === 'Enter' && !isTextareaFocused) {
      event.preventDefault();
      this.onSubmit();
      return;
    }

    // Skip quantity shortcuts when in input fields
    if (activeElement instanceof HTMLInputElement || isTextareaFocused) {
      return;
    }

    // Arrow Up or + key - increase quantity
    if (event.key === 'ArrowUp' || event.key === '+') {
      event.preventDefault();
      this.onQty('up');
      return;
    }

    // Arrow Down or - key - decrease quantity
    if (event.key === 'ArrowDown' || event.key === '-') {
      event.preventDefault();
      this.onQty('down');
      return;
    }
  }

  addToCartForm!: FormGroup;
  selectedExtra: Set<string> = new Set<string>();
  filteredFoodItemQuantityKeyValue = [] as KeyString[]
  extraTotalPrice: number = 0;
  constructor(@Inject(MAT_DIALOG_DATA) public data: {
                btnTitle: string, title: string, foodItem: ActivePos,
                foodItemQuantity: FoodItemQuantity[], regularQtyId: string
              },
              private dialogRef: MatDialogRef<AddToCartDialogComponent>,
              private store: Store<fromApp.AppState>) {
    if (this.data.foodItem.foodQuantityTagIds !== null) {
      const selectedQty = this.data.foodItemQuantity.filter(item =>
        this.data.foodItem.foodQuantityTagIds.includes(item.id)
      );
      this.filteredFoodItemQuantityKeyValue = FormHelper
        .ConvertForDropDown(selectedQty, {key: 'id', value: 'name'})
    }
  }

  ngOnInit() {
    this.formInit();
  }

  private formInit() {
    this.addToCartForm = new FormGroup({
      foodId: new FormControl(this.data?.foodItem?.foodId),
      menuId: new FormControl(this.data?.foodItem?.menuFoodId),
      kitchenId: new FormControl(this.data?.foodItem?.kitchenId),
      itemQuantity: new FormControl(1, [Validators.required]),
      kitchenInstruction: new FormControl(null),
      price: new FormControl(this.data?.foodItem?.foodBasePrice +
        (this.data.foodItem.foodFoodQuantityTags.find(x => x.foodQuantityTagId === this.data.regularQtyId)?.additionalPrice ?? 0)),
      totalCost: new FormControl(this.data?.foodItem?.totalCost +
        (this.data.foodItem.foodFoodQuantityTags.find(x => x.foodQuantityTagId === this.data.regularQtyId)?.additionalCost?? 0)),
      extras: new FormControl(null),
      foodQuantityTagId: new FormControl(this.filteredFoodItemQuantityKeyValue.map(x => x.key).includes(this.data.regularQtyId) ?
      this.data.regularQtyId : this.filteredFoodItemQuantityKeyValue.map(x => x.key)[0], [Validators.required]),
      isServiceCharge: new FormControl(true),
    });

    this.addToCartForm.get('foodQuantityTagId')?.valueChanges.subscribe(value => {
      // this.addToCartForm.get('price')?.setValue(this.data?.foodItem?.price *
      //   this.data.foodItemQuantity.find(x => x.id === value)?.pricingMultiple!)
      // this.addToCartForm.get('totalCost')?.setValue(this.data?.foodItem?.totalCost *
      //   this.data.foodItemQuantity.find(x => x.id === value)?.rawMaterialMultiple!)
      const selectedQty = this.data.foodItem.foodFoodQuantityTags.find(x => x.foodQuantityTagId === value);
      this.addToCartForm.get('price')?.setValue(this.data?.foodItem?.foodBasePrice + (selectedQty?.additionalPrice ?? 0));
      this.addToCartForm.get('totalCost')?.setValue(this.data?.foodItem?.totalCost + (selectedQty?.additionalCost ?? 0))
    });
  }

  onSubmit() {
    if (this.addToCartForm.valid) {
      const cartItem: CartRequestDetails = this.addToCartForm.value;
      cartItem.name = this.data?.foodItem?.foodName
      cartItem.foodId = this.addToCartForm.get('foodId')?.value
      cartItem.menuId = this.addToCartForm.get('menuId')?.value
      cartItem.kitchenId = this.addToCartForm.get('kitchenId')?.value
      cartItem.quantity = this.addToCartForm.get('itemQuantity')?.value
      cartItem.kitchenIntroduction = this.addToCartForm.get('kitchenInstruction')?.value
      cartItem.price = this.addToCartForm.get('price')?.value + this.extraTotalPrice
      cartItem.extras = this.addToCartForm.get('extras')?.value
      cartItem.totalCost = this.addToCartForm.get('totalCost')?.value
      cartItem.foodQuantityTagId = this.addToCartForm.get('foodQuantityTagId')?.value
      cartItem.isServiceCharge = this.addToCartForm.get('isServiceCharge')?.value
      this.store.dispatch(ADD_FOOD_TO_CART({payload: cartItem}))
      this.dialogRef.close(cartItem)
    } else {
      Object.values(this.addToCartForm.controls).forEach(control => {
        if (control.invalid) {
          control.markAsDirty();
          control.updateValueAndValidity({onlySelf: true})
        }
      })
    }
  }


  onExtraSelectionChange(id: string) {
    if (this.selectedExtra.has(id)) {
      this.selectedExtra.delete(id);
    } else {
      this.selectedExtra.add(id);
    }
    this.addToCartForm.get('extras')?.setValue(Array.from(this.selectedExtra))
    const extraItems = this.data.foodItem.foodExtras?.filter(x => this.selectedExtra.has(x.id))
    this.extraTotalPrice = extraItems?.reduce((acc, item) => acc + item.price!, 0)!
  }

  onQty(type: string) {
    if (type == 'up') {
      this.addToCartForm.get('itemQuantity')?.setValue(this.addToCartForm.get('itemQuantity')?.value + 1)
    } else {
      if (this.addToCartForm.get('itemQuantity')?.value > 1)
        this.addToCartForm.get('itemQuantity')?.setValue(this.addToCartForm.get('itemQuantity')?.value - 1)
    }
  }
}
